// components/SmartMaterialSearch.jsx
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import MaterialSearchModal from './MaterialSearchModal';

// ─── Fuzzy search helpers ───

function tokenise(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .map(w => w.trim())
    .filter(w => w.length >= 2);
}

function levenshtein(a, b) {
  const la = a.length, lb = b.length;
  if (la === 0) return lb;
  if (lb === 0) return la;
  let prev = Array.from({ length: lb + 1 }, (_, i) => i);
  let curr = new Array(lb + 1);
  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[lb];
}

function findClosestWords(token, vocabulary, maxDistance = 2, maxResults = 3) {
  const t = token.toLowerCase();
  const effectiveMax = t.length <= 3 ? Math.min(maxDistance, 1) : maxDistance;
  const candidates = [];
  for (const word of vocabulary) {
    if (Math.abs(word.length - t.length) > effectiveMax) continue;
    const dist = levenshtein(t, word);
    if (dist > 0 && dist <= effectiveMax) candidates.push({ word, distance: dist });
  }
  candidates.sort((a, b) => a.distance - b.distance);
  return candidates.slice(0, maxResults);
}

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'this', 'that', 'per',
  'mm', 'mpa', 'kg', 'm2', 'm3', 'thick', 'thin', 'type',
]);

const getName = (c) =>
  c?.MeasurementUnitName || c?.CostMeasurementName || c?.Name || c?.Description || '';

function buildIndex(materials) {
  const relatedTerms = {};
  const allTerms = new Set();
  const vocabSet = new Set();

  materials.forEach(m => {
    const name = getName(m);
    const cat = m.Category || '';
    const nameTokens = tokenise(name);
    const catTokens = tokenise(cat);

    allTerms.add(cat);
    nameTokens.filter(t => t.length > 3 && !STOP_WORDS.has(t)).forEach(t => allTerms.add(t));

    const allToks = [...new Set([...nameTokens, ...catTokens])];
    const display = [cat, ...nameTokens.filter(t => t.length > 3 && !STOP_WORDS.has(t))];

    allToks.forEach(tok => {
      display.forEach(dn => {
        if (tok === dn.toLowerCase()) return;
        if (STOP_WORDS.has(tok)) return;
        if (!relatedTerms[tok]) relatedTerms[tok] = new Set();
        relatedTerms[tok].add(dn);
      });
    });

    nameTokens.forEach(t => vocabSet.add(t));
    catTokens.forEach(t => vocabSet.add(t));
  });

  return { relatedTerms, allTerms: [...allTerms], vocabulary: [...vocabSet] };
}

function getSuggestions(query, index, maxSuggestions = 10) {
  if (!query.trim()) return [];
  const queryTokens = tokenise(query);
  if (queryTokens.length === 0) return [];

  const scored = {};
  queryTokens.forEach(qt => {
    Object.keys(index.relatedTerms).forEach(keyword => {
      if (keyword.includes(qt) || qt.includes(keyword)) {
        index.relatedTerms[keyword].forEach(term => {
          scored[term] = (scored[term] || 0) + 1;
        });
      }
    });
    index.allTerms.forEach(term => {
      if (term.toLowerCase().includes(qt)) scored[term] = (scored[term] || 0) + 0.5;
    });
    if (qt.length >= 4) {
      findClosestWords(qt, index.vocabulary, 2, 5).forEach(({ word, distance }) => {
        Object.keys(index.relatedTerms).forEach(keyword => {
          if (keyword === word || keyword.includes(word) || word.includes(keyword)) {
            index.relatedTerms[keyword].forEach(term => {
              scored[term] = (scored[term] || 0) + 1 / (distance + 1);
            });
          }
        });
      });
    }
  });

  const qLower = query.toLowerCase().trim();
  delete scored[qLower];

  return Object.entries(scored)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxSuggestions)
    .map(([term]) => term);
}

function matchesFuzzy(query, candidate) {
  const tokens = tokenise(query);
  if (tokens.length === 0) return true;
  const haystack = candidate.toLowerCase();
  const haystackWords = tokenise(haystack);

  return tokens.every(token => {
    if (haystack.includes(token)) return true;
    const maxDist = token.length <= 4 ? 1 : 2;
    return haystackWords.some(word => {
      if (Math.abs(word.length - token.length) > maxDist) return false;
      return levenshtein(token, word) <= maxDist;
    });
  });
}

// ─── Styles ───

const styles = {
  container: { position: 'relative', width: '100%' },
  inputWrapper: {
    display: 'flex', alignItems: 'center',
    border: '1px solid #ddd', borderRadius: 8, height: 44,
    padding: '0 10px', background: '#fff',
    fontFamily: "'Gotham Rounded', sans-serif", fontSize: 14,
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  inputWrapperFocused: {
    borderColor: '#4A9B8F',
    boxShadow: '0 0 0 2px rgba(74,155,143,.15)',
  },
  inputWrapperWithValue: {
    borderColor: '#4A9B8F', background: '#F0FAF8',
  },
  input: {
    flex: 1, border: 'none', outline: 'none', fontSize: 14,
    fontFamily: "'Gotham Rounded', sans-serif", background: 'transparent', color: '#243c4c',
  },
  searchIcon: { marginRight: 8, color: '#6B7280', fontSize: 16, flexShrink: 0 },
  clearBtn: {
    border: 'none', background: 'none', cursor: 'pointer', color: '#9CA3AF',
    fontSize: 16, padding: '0 4px', lineHeight: 1, flexShrink: 0,
  },
  expandBtn: {
    border: '1px solid #E0E0E0', background: '#F5F7FA', cursor: 'pointer',
    color: '#4A9B8F', fontSize: 14, fontWeight: 700, padding: '2px 6px',
    borderRadius: 4, lineHeight: 1, flexShrink: 0, marginLeft: 4,
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
    background: '#fff', border: '1px solid #ddd', borderRadius: 8,
    marginTop: 4, maxHeight: 360, overflowY: 'auto',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
  suggestionsBar: {
    display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0', background: '#FAFBFC',
  },
  suggestionChip: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 12,
    fontSize: 11, fontWeight: 500, cursor: 'pointer',
    border: '1px solid #E0E0E0', background: '#fff', color: '#555',
  },
  suggestionChipActive: { background: '#4A9B8F', color: '#fff', borderColor: '#4A9B8F' },
  spellingBar: {
    padding: '6px 12px', borderBottom: '1px solid #f0f0f0', background: '#FFF8E1',
    fontSize: 12, color: '#666',
  },
  spellingLink: {
    color: '#4A9B8F', fontWeight: 600, cursor: 'pointer',
    textDecoration: 'underline', textDecorationStyle: 'dotted',
  },
  categoryHeader: {
    padding: '6px 12px', fontSize: 11, fontWeight: 700, color: '#4A9B8F',
    background: '#F0FAF8', textTransform: 'uppercase', letterSpacing: '0.04em',
    position: 'sticky', top: 0, zIndex: 1,
  },
  resultItem: {
    padding: '8px 12px 8px 20px', fontSize: 13, cursor: 'pointer',
    color: '#243c4c', borderBottom: '1px solid #f8f8f8', lineHeight: 1.4,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  resultItemHover: { background: '#E6F3F7' },
  noResults: { padding: '16px 12px', fontSize: 13, color: '#999', textAlign: 'center' },
  selectedDisplay: {
    display: 'flex', alignItems: 'center', gap: 6, flex: 1, overflow: 'hidden',
  },
  selectedCat: {
    fontSize: 10, fontWeight: 600, color: '#4A9B8F', background: '#E6F3F7',
    padding: '2px 6px', borderRadius: 4, flexShrink: 0, whiteSpace: 'nowrap',
  },
  selectedName: {
    fontSize: 13, color: '#243c4c', overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  resultCount: {
    padding: '6px 12px', fontSize: 11, color: '#999',
    borderTop: '1px solid #f0f0f0', textAlign: 'center', background: '#FAFBFC',
  },
};

// ─── Component ───

const SmartMaterialSearch = ({
  materials = [],
  carbonCategories = [],
  biogenicCategories = [],
  weightCategories = [],
  value = '',
  category = '',
  onChange,
  placeholder = 'Search materials...',
  name = '',
  showMetrics = true,
}) => {
  const [query, setQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  const [activeSuggestions, setActiveSuggestions] = useState([]);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const keywordIndex = useMemo(() => buildIndex(materials), [materials]);
  const vocabulary = useMemo(() => keywordIndex.vocabulary, [keywordIndex]);
  const suggestions = useMemo(() => getSuggestions(query, keywordIndex), [query, keywordIndex]);

  const spellingCorrections = useMemo(() => {
    if (!query.trim()) return [];
    const tokens = tokenise(query);
    const corrections = [];
    tokens.forEach(token => {
      const hasMatch = vocabulary.some(v => v.includes(token) || token.includes(v));
      if (!hasMatch) {
        const closest = findClosestWords(token, vocabulary);
        if (closest.length > 0) corrections.push({ original: token, suggestions: closest.map(c => c.word) });
      }
    });
    return corrections;
  }, [query, vocabulary]);

  const filteredResults = useMemo(() => {
    const combinedQuery = [query, ...activeSuggestions].join(' ').trim();
    if (!combinedQuery && !category) {
      const grouped = {};
      materials.forEach(m => {
        const cat = m.Category || 'Other';
        const mName = getName(m);
        if (!mName) return;
        if (!grouped[cat]) grouped[cat] = [];
        if (grouped[cat].length < 5) grouped[cat].push({ name: mName, category: cat, raw: m });
      });
      return { groups: grouped, total: materials.length };
    }

    const results = [];
    const seen = new Set();
    materials.forEach(m => {
      const mName = getName(m);
      const cat = m.Category || '';
      const key = m.DedupeKey || mName; // see MaterialSearchModal: same-named items stay separate when keyed
      if (!mName || seen.has(key)) return;
      if (matchesFuzzy(combinedQuery, `${mName} ${cat} ${m.SearchKeys || ''}`)) {
        seen.add(key);
        results.push({ name: mName, category: cat, raw: m });
      }
    });

    const grouped = {};
    results.forEach(r => {
      if (!grouped[r.category]) grouped[r.category] = [];
      grouped[r.category].push(r);
    });

    return { groups: grouped, total: results.length };
  }, [materials, query, activeSuggestions, category]);

  const flatResults = useMemo(() => {
    const flat = [];
    Object.entries(filteredResults.groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([, items]) => { items.forEach(item => flat.push(item)); });
    return flat;
  }, [filteredResults]);

  // Close dropdown on outside click — but NOT when modal is open
  useEffect(() => {
    const handleClick = (e) => {
      if (modalOpen) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [modalOpen]);

  const handleKeyDown = useCallback((e) => {
    if (!dropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { setDropdownOpen(true); e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHoveredIdx(prev => Math.min(prev + 1, flatResults.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHoveredIdx(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter' && hoveredIdx >= 0) { e.preventDefault(); const item = flatResults[hoveredIdx]; if (item) handleSelect(item); }
    else if (e.key === 'Escape') { setDropdownOpen(false); }
  }, [dropdownOpen, hoveredIdx, flatResults]);

  const handleSelect = (item) => {
    onChange(item.name, item.category, item.raw);
    setQuery('');
    setActiveSuggestions([]);
    setDropdownOpen(false);
    setHoveredIdx(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', '');
    setQuery('');
    setActiveSuggestions([]);
    setDropdownOpen(false);
  };

  const openModal = (e) => {
    e.stopPropagation();
    // Close dropdown first, then open modal
    setDropdownOpen(false);
    setIsFocused(false);
    // Small delay to let dropdown close before modal opens
    setTimeout(() => setModalOpen(true), 50);
  };

  const handleModalSelect = (desc, cat, rawItem) => {
    onChange(desc, cat, rawItem);
    setModalOpen(false);
    setQuery('');
    setActiveSuggestions([]);
  };

  const toggleSuggestion = (term) => {
    setActiveSuggestions(prev =>
      prev.includes(term) ? prev.filter(t => t !== term) : [...prev, term]
    );
  };

  const applyCorrection = (original, corrected) => {
    const regex = new RegExp(original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    setQuery(prev => prev.replace(regex, corrected));
  };

  const hasValue = value && value.trim();

  // Selected state — compact display
  if (hasValue && !dropdownOpen) {
    return (
      <div ref={containerRef} style={styles.container}>
        <div
          style={{ ...styles.inputWrapper, ...styles.inputWrapperWithValue, cursor: 'pointer' }}
          onClick={() => { setDropdownOpen(true); setIsFocused(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        >
          <span style={styles.searchIcon}>🔍</span>
          <div style={styles.selectedDisplay}>
            {category && <span style={styles.selectedCat}>{category}</span>}
            <span style={styles.selectedName} title={value}>{value}</span>
          </div>
          <button style={styles.expandBtn} onClick={openModal} title="Browse all materials">⤢</button>
          <button style={styles.clearBtn} onClick={handleClear} title="Clear">✕</button>
        </div>

        <MaterialSearchModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSelect={handleModalSelect}
          materials={materials}
          carbonCategories={carbonCategories}
          biogenicCategories={biogenicCategories}
          weightCategories={weightCategories}
          title="Search Materials"
        showMetrics={showMetrics}
        />
      </div>
    );
  }

  // Search state — input with dropdown
  return (
    <div ref={containerRef} style={styles.container}>
      <div style={{ ...styles.inputWrapper, ...(isFocused ? styles.inputWrapperFocused : {}) }}>
        <span style={styles.searchIcon}>🔍</span>
        <input
          ref={inputRef}
          name={name}
          style={styles.input}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setDropdownOpen(true); setHoveredIdx(-1); }}
          onFocus={() => { setIsFocused(true); setDropdownOpen(true); }}
          onKeyDown={handleKeyDown}
        />
        {(query || activeSuggestions.length > 0) && (
          <button style={styles.clearBtn} onClick={() => { setQuery(''); setActiveSuggestions([]); }} title="Clear search">✕</button>
        )}
        <button style={styles.expandBtn} onClick={openModal} title="Browse all materials">⤢</button>
      </div>

      {/* Dropdown */}
      {dropdownOpen && !modalOpen && (
        <div style={styles.dropdown}>
          {spellingCorrections.length > 0 && (
            <div style={styles.spellingBar}>
              Did you mean:{' '}
              {spellingCorrections.map(c => (
                <span key={c.original}>
                  {c.suggestions.map((s, idx) => (
                    <span key={s}>
                      <span style={styles.spellingLink} onClick={() => applyCorrection(c.original, s)}>{s}</span>
                      {idx < c.suggestions.length - 1 && ' / '}
                    </span>
                  ))}
                  <span style={{ color: '#999', marginLeft: 4 }}>(for "{c.original}")</span>
                </span>
              ))}
            </div>
          )}

          {suggestions.length > 0 && (
            <div style={styles.suggestionsBar}>
              <span style={{ fontSize: 11, color: '#999', marginRight: 4 }}>Related:</span>
              {suggestions.map(term => {
                const isActive = activeSuggestions.includes(term);
                return (
                  <span key={term} style={{ ...styles.suggestionChip, ...(isActive ? styles.suggestionChipActive : {}) }}
                    onClick={() => toggleSuggestion(term)}>
                    {term}
                  </span>
                );
              })}
            </div>
          )}

          {activeSuggestions.length > 0 && (
            <div style={{ ...styles.suggestionsBar, background: '#fff', borderBottom: '1px solid #E0E0E0' }}>
              <span style={{ fontSize: 11, color: '#666', marginRight: 4 }}>Filters:</span>
              {activeSuggestions.map(term => (
                <span key={term} style={{ ...styles.suggestionChip, ...styles.suggestionChipActive }}
                  onClick={() => toggleSuggestion(term)}>
                  {term} ✕
                </span>
              ))}
              <span style={{ ...styles.suggestionChip, color: '#999' }} onClick={() => setActiveSuggestions([])}>Clear all</span>
            </div>
          )}

          {Object.entries(filteredResults.groups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([cat, items]) => (
              <div key={cat}>
                <div style={styles.categoryHeader}>{cat} ({items.length})</div>
                {items.slice(0, 20).map((item, idx) => {
                  const flatIdx = flatResults.indexOf(item);
                  return (
                    <div key={`${item.name}-${idx}`}
                      style={{ ...styles.resultItem, ...(flatIdx === hoveredIdx ? styles.resultItemHover : {}) }}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setHoveredIdx(flatIdx)}
                      title={item.name}>
                      {item.name}
                    </div>
                  );
                })}
              </div>
            ))}

          {filteredResults.total === 0 && (query || activeSuggestions.length > 0) && (
            <div style={styles.noResults}>No materials found. Try different search terms.</div>
          )}

          {filteredResults.total > 0 && (
            <div style={styles.resultCount}>
              {filteredResults.total} material{filteredResults.total !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}

      {/* Modal (rendered via portal in MaterialSearchModal) */}
      <MaterialSearchModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelect={handleModalSelect}
        materials={materials}
        carbonCategories={carbonCategories}
        biogenicCategories={biogenicCategories}
        weightCategories={weightCategories}
        title="Search Materials"
        showMetrics={showMetrics}
      />
    </div>
  );
};

export default SmartMaterialSearch;